/**
 * The plan's times in one centered dialog: the country's timezone (the
 * recipients' clock every other time is on), the sending window and the
 * layout time — each time echoed in Danish under its picker — with a live
 * day-strip of the window being edited, and the containers that would have
 * drop times outside it (18/10/2026). A close before the opening is the
 * next day (the same time is the whole day): "+1 day" beside it, and the way to get there said under it
 * (26/10/2026, client: "Allow window to close following day +1").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useEffect, useState } from 'react';
import { CalendarClock, Sunrise, Sunset } from 'lucide-react';

import { ConfirmDialog, HintDot } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { usePlanTimezone } from '../../../hooks/usePlanTimezone';
import { useDropGeneratorMutations } from '../../../useDropGeneratorMutations';
import { hhmmToMinutes, minutesToHHMM } from '../../../utils/format';
import type { DropGenContainerSummary } from '../../../drop-generator.types';
import { DayStrip } from '../../shared/DayStrip';
import { WindowImpact } from './impact/WindowImpact';
import { TimeField } from './TimeField';
import { TimezoneField } from './TimezoneField';

interface Props {
  open: boolean;
  countryKey: string;
  recipientTimezone: string | null;
  windowStartMin: number;
  windowEndMin: number;
  generateAtMin: number;
  /** 18/10/2026 — the plan's containers, so the dialog says which fall outside the window being edited. */
  containers?: DropGenContainerSummary[];
  onClose: () => void;
}

const LAYOUT_HINT =
  'Every day at this time the generator WRITES the whole day as draft drops — which container, when, how much. Nothing is sent at that moment: each drop goes out later, at its own slot time inside the window.';

/** Under the close picker: before the opening = the next day; the same time = the whole day (shared `inCountryWindow`). */
const closeHint = (startMin: number, endMin: number): string =>
  endMin < startMin
    ? 'Closes the next day — the window runs past midnight.'
    : endMin === startMin
      ? 'The same time as the opening — the window is open the whole day.'
      : 'To close the next day, pick a time earlier than the opening.';

function footnote(tz: string | null, wraps: boolean): string {
  if (!tz) return "Pick the country's timezone first — until then these times cannot be translated and are taken as they are.";
  const base = `You pick these in Danish time 🇩🇰; the country's time is under each. They are the country's own rules, saved in ${tz} time (the recipients' clock) — so their Danish time moves when either country changes its clocks.`;
  const hard = 'No drop is planned outside the window — a hard limit, optimizer extras included — unless a container or a single drop lifts it (Schedule › Outside the window).';
  return wraps ? `${base} This window wraps past midnight — it closes on the next calendar day. ${hard}` : `${base} ${hard}`;
}

export const PlanWindowDialog: FC<Props> = (props) => {
  const [tz, setTz] = useState(props.recipientTimezone);
  const [start, setStart] = useState(minutesToHHMM(props.windowStartMin));
  const [end, setEnd] = useState(minutesToHHMM(props.windowEndMin));
  const [generateAt, setGenerateAt] = useState(minutesToHHMM(props.generateAtMin));
  const { updatePlan } = useDropGeneratorMutations();
  const setTimezone = usePlanTimezone();
  const { success, error } = useToast();

  useEffect(() => {
    if (!props.open) return;
    setTz(props.recipientTimezone);
    setStart(minutesToHHMM(props.windowStartMin));
    setEnd(minutesToHHMM(props.windowEndMin));
    setGenerateAt(minutesToHHMM(props.generateAtMin));
  }, [props.open, props.recipientTimezone, props.windowStartMin, props.windowEndMin, props.generateAtMin]);

  const startMin = hhmmToMinutes(start) ?? props.windowStartMin;
  const endMin = hhmmToMinutes(end) ?? props.windowEndMin;
  const tzChanged = tz !== null && tz !== props.recipientTimezone;

  const save = async () => {
    try {
      if (tzChanged) await setTimezone.mutateAsync({ countryKey: props.countryKey, recipientTimezone: tz });
      await updatePlan.mutateAsync({
        countryKey: props.countryKey,
        windowStartMin: startMin,
        windowEndMin: endMin,
        generateAtMin: hhmmToMinutes(generateAt) ?? props.generateAtMin,
      });
      success('Plan updated', tzChanged ? `Timezone ${tz}, window and layout time saved.` : 'Window and layout time saved.');
      props.onClose();
    } catch (err) {
      error('Could not save the plan', getApiErrorMessage(err));
    }
  };

  return (
    <ConfirmDialog
      open={props.open}
      title="Timezone, sending window & layout time"
      widthClass="max-w-xl"
      description={
        <div className="flex flex-col gap-5 pt-2">
          <TimezoneField countryKey={props.countryKey} value={tz} onChange={setTz} />
          <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
            <TimeField label={<><Sunrise className="h-3 w-3" /> Window opens</>} value={start} onChange={setStart} recipientTimezone={tz} />
            <div className="flex flex-col gap-1">
              <TimeField label={<><Sunset className="h-3 w-3" /> Window closes</>} value={end} onChange={setEnd} recipientTimezone={tz} nextDay={endMin < startMin} />
              <span className="max-w-[13rem] text-[11px] leading-snug text-muted-foreground" data-testid="close-next-day-hint">
                {closeHint(startMin, endMin)}
              </span>
            </div>
            <TimeField
              label={<><CalendarClock className="h-3 w-3" /> Plan the day at <HintDot label={LAYOUT_HINT} /></>}
              value={generateAt}
              onChange={setGenerateAt}
              recipientTimezone={tz}
            />
          </div>
          <DayStrip windowStartMin={startMin} windowEndMin={endMin} recipientTimezone={tz} spanStartMin={null} spanEndMin={null} spanLabel="containers appear here once scheduled" slotMinutes={[]} />
          {props.containers ? (
            <WindowImpact containers={props.containers} tz={tz} draft={{ startMin, endMin }} saved={{ startMin: props.windowStartMin, endMin: props.windowEndMin }} />
          ) : null}
          <p className="text-xs text-muted-foreground">{footnote(tz, endMin < startMin)}</p>
        </div>
      }
      confirmLabel="Save"
      busy={updatePlan.isPending || setTimezone.isPending}
      onConfirm={save}
      onCancel={props.onClose}
    />
  );
};
